import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import showPwd from '../assets/images/icons/show_pwd.png'
import hidePwd from '../assets/images/icons/hide_pwd.png'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const ChangePasswordModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()

  const [loading, setLoading] = useState(false)

  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [message, setMessage] = useState(null)

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  const [passwordChanged, setPasswordChanged] = useState(false)

  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
const [canClose, setCanClose] = useState(false)

const validatePassword = (pwd) => {
  if (pwd.length < 10) {
    return t('PASSWORD_TOO_SHORT')
  }

  if (!/[A-Z]/.test(pwd)) {
    return t('PASSWORD_NEEDS_UPPER')
  }

  if (!/[0-9]/.test(pwd)) {
    return t('PASSWORD_NEEDS_NUMBER')
  }

  if (!/[!@#$%^&*(),.?":{}|<>_\-\\[\]/+=~`]/.test(pwd)) {
    return t('PASSWORD_NEEDS_SPECIAL')
  }

  return null
}

  const clearModal = () => {
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setMessage(null)

    setShowOldPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)

    setPasswordChanged(false)
  }

const closeModal = useCallback(() => {
  clearModal()
  onClose()
}, [onClose])

useEffect(() => {
  if (!open) return

  const handleEsc = (e) => {
    if (e.key === 'Escape') {
      closeModal()
    }
  }

  window.addEventListener('keydown', handleEsc)

  return () => {
    window.removeEventListener('keydown', handleEsc)
  }
}, [open, closeModal])

  const changePassword = async () => {
    if (!oldPassword) {
      setMessage({
        message: t('OLD-PASSWORD-MISSING'),
        type: 'error'
      })
      return
    }

    if (!newPassword || !confirmPassword) {
      setMessage({
        message: t('PASSWORD_CHANGE_FAILED'),
        type: 'error'
      })
      return
    }

    const validationError = validatePassword(newPassword)

    if (validationError) {
      setMessage({
        message: validationError,
        type: 'error'
      })
      return
    }

    if (newPassword !== confirmPassword) {
      setMessage({
        message: t('PASSWORD-MISMATCH'),
        type: 'error'
      })
      return
    }

    try {
      setLoading(true)
      setMessage(null)

      const res = await fetchWithAuth('/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        body: JSON.stringify({
          oldPassword,
          newPassword
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setMessage({
          message: data?.message || t('PASSWORD_CHANGE_FAILED'),
          type: 'error'
        })
        return
      }

      setMessage({
        message: t('PASSWORD_CHANGED'),
        type: 'success'
      })

      setPasswordChanged(true)

    } catch {
      setMessage({
        message: t('PASSWORD_CHANGE_FAILED'),
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  const fields = [
    {
      key: 'oldPassword',
      value: oldPassword,
      setValue: setOldPassword,
      label: t('OLD_PASSWORD'),
      shown: showOldPassword,
      setShown: setShowOldPassword
    },
    {
      key: 'newPassword',
      value: newPassword,
      setValue: setNewPassword,
      label: t('NEW_PASSWORD'),
      shown: showNewPassword,
      setShown: setShowNewPassword
    },
    {
      key: 'confirmPassword',
      value: confirmPassword,
      setValue: setConfirmPassword,
      label: t('CONFIRM_PASSWORD'),
      shown: showConfirmPassword,
      setShown: setShowConfirmPassword
    }
  ]

  return (
    <>
      <Overlay open={loading} zIndex={4500} backdropOpacity={0.75} />

<div
  onMouseDown={(e) => {
    setCanClose(e.target === e.currentTarget)
  }}
  onMouseUp={(e) => {
    if (canClose && e.target === e.currentTarget) {
      closeModal()
    }

    setCanClose(false)
  }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3500
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 'clamp(320px, 30vw, 420px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h3 style={{ fontSize: 28, margin: 0, marginBottom: 24 }}>
            {t('CHANGE_PASSWORD')}
          </h3>

          {message && (
            <MessageBox
              message={message.message}
              type={message.type}
            />
          )}

          <div style={{ width: '100%' }}>

            <input
              type="text"
              autoComplete="username"
              style={{ display: 'none' }}
            />

            <input
              type="password"
              autoComplete="current-password"
              style={{ display: 'none' }}
            />

            {fields.map(field => (
              <input
                key={field.key}
                disabled={passwordChanged}
                autoComplete="off"
                type={field.shown ? 'text' : 'password'}
                placeholder={field.label}
                value={field.value}
                onChange={e => field.setValue(e.target.value)}
                onClick={(e) => {
                  const rect = e.target.getBoundingClientRect()

                  if (e.clientX > rect.right - 40) {
                    field.setShown(prev => !prev)
                  }
                }}
                onMouseMove={(e) => {
                  const rect = e.target.getBoundingClientRect()

                  e.target.style.cursor =
                    e.clientX > rect.right - 40
                      ? 'pointer'
                      : 'text'
                }}
                style={{
                  width: '100%',
                  height: 44,
                  fontSize: 15,
                  padding: '0 18px',
                  marginBottom: 12,
                  border: '1.5px solid #bfbfbf',
                  boxSizing: 'border-box',
                  background: passwordChanged ? '#ddd' : '#fff',
                  backgroundImage: `url(${field.shown ? hidePwd : showPwd})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '20px'
                }}
              />
            ))}

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'center',
                marginTop: 8
              }}
            >
              {passwordChanged ? (
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'close-password',
                    hoverBtn === 'close-password'
                  )}
                  onMouseEnter={() => setHoverBtn('close-password')}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn('close-password')}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={closeModal}
                >
                  {t('CLOSE')}
                </button>
              ) : (
                <>
                  <button
                    style={modalPrimaryButtonStyle(
                      pressedBtn,
                      'save-password',
                      hoverBtn === 'save-password'
                    )}
                    onMouseEnter={() => setHoverBtn('save-password')}
                    onMouseLeave={() => {
                      setHoverBtn(null)
                      setPressedBtn(null)
                    }}
                    onMouseDown={() => setPressedBtn('save-password')}
                    onMouseUp={() => setPressedBtn(null)}
                    onClick={changePassword}
                  >
                    {t('SAVE')}
                  </button>

                  <button
                    style={modalSecondaryButtonStyle(
                      pressedBtn,
                      'cancel-password',
                      hoverBtn === 'cancel-password'
                    )}
                    onMouseEnter={() => setHoverBtn('cancel-password')}
                    onMouseLeave={() => {
                      setHoverBtn(null)
                      setPressedBtn(null)
                    }}
                    onMouseDown={() => setPressedBtn('cancel-password')}
                    onMouseUp={() => setPressedBtn(null)}
                    onClick={closeModal}
                  >
                    {t('CANCEL')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ChangePasswordModal